import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import {
    IAuditLogFilters,
    IAuditLogResponse,
    IAuditLogTimelineResponse,
    IAuditLogStatsResponse
} from "../../utils/auditLog.types";

/**
 * Fetch audit logs with filters and pagination
 */
export const fetchAuditLogs = createAsyncThunk(
    'auditLog/fetchAuditLogs',
    async (filters: IAuditLogFilters): Promise<IAuditLogResponse> => {
        const paramsArray: string[][] = [];

        if (filters.entity) paramsArray.push(['entity', filters.entity]);
        if (filters.entityId) paramsArray.push(['entityId', filters.entityId]);
        if (filters.userId) paramsArray.push(['userId', filters.userId]);
        if (filters.action) paramsArray.push(['action', filters.action]);
        if (filters.module) paramsArray.push(['module', filters.module]);
        if (filters.source) paramsArray.push(['source', filters.source]);
        if (filters.from) paramsArray.push(['from', filters.from]);
        if (filters.to) paramsArray.push(['to', filters.to]);
        if (filters.search) paramsArray.push(['search', filters.search]);
        if (filters.page !== undefined) paramsArray.push(['page', filters.page.toString()]);
        if (filters.limit !== undefined) paramsArray.push(['limit', filters.limit.toString()]);

        const params = new URLSearchParams(paramsArray);
        const response = await axios.get(`/api/v1/audit-log`, { params });

        const logs = response.data.logs || [];
        const pagination = response.data.pagination || { total: 0, page: 1, limit: 20, pages: 0 };

        return { success: true, logs, pagination };
    }
);

/**
 * Fetch timeline for a specific entity
 */
export const fetchEntityTimeline = createAsyncThunk(
    'auditLog/fetchEntityTimeline',
    async ({ entity, entityId }: { entity: string; entityId: string }): Promise<IAuditLogTimelineResponse> => {
        const response = await axios.get(`/api/v1/audit-log/timeline/${entity}/${entityId}`);
        return response.data;
    }
);

/**
 * Fetch modules (each with its entities) and users present in the logs (filter dropdowns)
 */
export const fetchAuditFilterOptions = createAsyncThunk(
    'auditLog/fetchAuditFilterOptions',
    async (): Promise<{ modules: { module: string; entities: string[] }[]; users: { _id: string; name: string; email: string }[] }> => {
        const response = await axios.get(`/api/v1/audit-log/filter-options`);
        return { modules: response.data.data?.modules || [], users: response.data.data?.users || [] };
    }
);

/**
 * Fetch audit stats
 */
export const fetchAuditStats = createAsyncThunk(
    'auditLog/fetchAuditStats',
    async ({ from, to }: { from?: string; to?: string }): Promise<IAuditLogStatsResponse> => {
        const paramsArray: string[][] = [];
        if (from) paramsArray.push(['from', from]);
        if (to) paramsArray.push(['to', to]);

        const params = new URLSearchParams(paramsArray);
        const response = await axios.get(`/api/v1/audit-log/stats`, { params });
        return response.data;
    }
);
